import { useState, useCallback, useRef } from 'react';
import UserHistory from '../data/userHistories';
import { metadata } from '../data/metadata';
import useAuth from './useAuth';

const { UserHistoryRequest } = require('../grpc/generated/account_pb');

/**
 * Custom hook สำหรับ fetch history data ด้วย smartFilter
 * @returns {Object} { fetchHistory, loading, error, data }
 */
const useHistorySmartFilter = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const { activeAccount } = useAuth();
  const debounceTimeoutRef = useRef(null);

  /**
   * Fetch history data ด้วย smartFilter พร้อม debounce 2 วินาที
   * @param {number} mode - Mode (1=deals, 2=positions, 3=orders, 4=all)
   * @param {number} periodKey - Period key (7=today, 1=1month, 2=3months, 3=6months, 4=1year, 5=custom)
   * @param {string} accountId - Account ID (optional, จะใช้ activeAccount ถ้าไม่ระบุ)
   * @param {number} fromCustom - Custom start timestamp (optional, สำหรับ custom period)
   * @param {number} toCustom - Custom end timestamp (optional, สำหรับ custom period)
   * @returns {Promise<Array>} History data
   */
  const fetchHistory = useCallback(
    async (mode = 4, periodKey = 7, accountId = null, fromCustom = null, toCustom = null) => {
      return new Promise((resolve, reject) => {
        // Clear previous timeout
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }

        // Set new timeout for 2 seconds debounce
        debounceTimeoutRef.current = setTimeout(async () => {
          setLoading(true);
          setError(null);

          try {
            const history = new UserHistory();
            const request = new UserHistoryRequest();
            const targetAccountId = accountId || activeAccount;

            if (!targetAccountId) {
              throw new Error('Account ID is required');
            }

            const result = await history.smartFilter(
              request,
              metadata(),
              mode,
              periodKey,
              targetAccountId,
              fromCustom,
              toCustom
            );

            setData(result);
            resolve(result);
          } catch (err) {
            console.error('[useHistorySmartFilter] Error:', err);
            setError(err.message || 'Failed to fetch history data');
            reject(err);
          } finally {
            setLoading(false);
          }
        }, 2000); // 2 seconds debounce
      });
    },
    [activeAccount]
  );

  /**
   * Fetch deals history (mode = 1)
   */
  const fetchDeals = useCallback(
    (periodKey = 7, accountId = null, fromCustom = null, toCustom = null) => {
      return fetchHistory(1, periodKey, accountId, fromCustom, toCustom);
    },
    [fetchHistory]
  );

  /**
   * Fetch positions history (mode = 2)
   */
  const fetchPositions = useCallback(
    (periodKey = 7, accountId = null, fromCustom = null, toCustom = null) => {
      return fetchHistory(2, periodKey, accountId, fromCustom, toCustom);
    },
    [fetchHistory]
  );

  /**
   * Fetch orders history (mode = 3)
   */
  const fetchOrders = useCallback(
    (periodKey = 7, accountId = null, fromCustom = null, toCustom = null) => {
      return fetchHistory(3, periodKey, accountId, fromCustom, toCustom);
    },
    [fetchHistory]
  );

  /**
   * Fetch all history (mode = 4)
   */
  const fetchAll = useCallback(
    (periodKey = 7, accountId = null, fromCustom = null, toCustom = null) => {
      return fetchHistory(4, periodKey, accountId, fromCustom, toCustom);
    },
    [fetchHistory]
  );

  /**
   * Clear current data and error
   */
  const clearData = useCallback(() => {
    setData([]);
    setError(null);
    // Clear any pending debounce timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
  }, []);

  return {
    // Main function
    fetchHistory,

    // Convenience functions
    fetchDeals,
    fetchPositions,
    fetchOrders,
    fetchAll,

    // State
    loading,
    error,
    data,

    // Utilities
    clearData
  };
};

export default useHistorySmartFilter;
